/**
 * Public Services & Pricing offers. Prices and the refund sentence come from
 * offerCatalog.json, which the marketing workflow also reads
 * (marketing/src/offer_catalog.py). Change prices there, not here.
 * Keep copy plain-language; avoid performance/tax-savings guarantees.
 *
 * OPEN COMPLIANCE / BUSINESS QUESTIONS (do not invent answers in copy):
 * - Whether investment-management or third-party fees are included in listed prices
 * - Whether ongoing service has a minimum term, setup fee, or cancellation requirements
 * - Whether “approximately two weeks of service” means turnaround, access window, or elapsed engagement
 * - Precise meaning and scope of “investment reports”
 * - Whether tax preparation, legal work, or discretionary investment management is excluded
 * - Whether all public descriptions align with the current Form ADV, client agreement, and fee schedule
 */

import offerCatalog from './offerCatalog.json';

export const SERVICES_PATH = offerCatalog.paths.services;
export const SERVICES_ALIAS_PATH = offerCatalog.paths.servicesAlias;
export const INTAKE_PATH = offerCatalog.paths.intake;
/** Goal-Setting Meeting details page and its booking page. */
export const MEETING_PATH = offerCatalog.paths.meeting;
export const BOOKING_PATH = offerCatalog.paths.booking;

export function formatUsd(amount) {
  return `$${Number(amount).toLocaleString('en-US')}`;
}

/** Formatted prices from offerCatalog.json. */
export const PRICES = Object.freeze({
  comprehensive: formatUsd(offerCatalog.offers.comprehensive_plan.price),
  ongoingMonthly: formatUsd(offerCatalog.offers.ongoing_planning.price),
  planningMeeting: formatUsd(offerCatalog.goalSettingMeeting.price),
});

/** The paid first meeting. Every term here comes from the catalog. */
export const GOAL_SETTING_MEETING = Object.freeze({
  offerId: 'goal_setting_meeting',
  name: offerCatalog.goalSettingMeeting.name,
  cta: offerCatalog.goalSettingMeeting.cta,
  price: offerCatalog.goalSettingMeeting.price,
  priceLabel: formatUsd(offerCatalog.goalSettingMeeting.price),
  durationMinutes: offerCatalog.goalSettingMeeting.durationMinutes,
  refundWording: offerCatalog.goalSettingMeeting.refundWording,
  creditWording: offerCatalog.goalSettingMeeting.creditWording,
  creditWindowDays: offerCatalog.goalSettingMeeting.creditWindowDays,
  cancellationWording: offerCatalog.goalSettingMeeting.cancellationWording,
  /** Plan fee left after the meeting credit. */
  planBalanceLabel: formatUsd(
    offerCatalog.offers.comprehensive_plan.price - offerCatalog.goalSettingMeeting.price
  ),
});

/** Who the planning offers are built for (approximate; income alone does not establish fit). */
export const AUDIENCE = Object.freeze({
  householdIncomeLabel: formatUsd(offerCatalog.audience.householdIncome),
  individualIncomeLabel: formatUsd(offerCatalog.audience.individualIncome),
});

/** New comprehensive planning clients taken on per month (not a limit on meetings). */
export const NEW_PLAN_CLIENTS_PER_MONTH = offerCatalog.capacity.newComprehensiveClientsPerMonth;

/** Published refund sentence for the paid first meeting. */
export const PLANNING_MEETING_REFUND_WORDING = offerCatalog.goalSettingMeeting.refundWording;

/** Canonical offer IDs used in analytics and deep links. */
export const OFFER_IDS = Object.freeze({
  GOAL_SETTING_MEETING: 'goal_setting_meeting',
  COMPREHENSIVE: 'comprehensive_plan',
  ONGOING: 'ongoing_planning',
});

export const PRICING_MODELS = Object.freeze({
  FIXED: 'fixed',
  MONTHLY: 'monthly',
});

export const CONTENT_CATEGORIES = Object.freeze({
  BUDGET: 'budget',
  TAX: 'tax',
  INVESTMENT: 'investment',
  RISK: 'risk',
  RETIREMENT: 'retirement',
  HOUSING: 'housing',
  GENERAL: 'general',
  EDUCATIONAL: 'educational',
});

export const PRIMARY_WAYS = [
  {
    id: 'comprehensive',
    title: 'Comprehensive financial plan',
    summary: 'A coordinated roadmap across cash management, risk management, and wealth management.',
    priceLabel: PRICES.comprehensive,
  },
  {
    id: 'ongoing',
    title: 'Ongoing planning',
    summary: 'Implementation support, reporting, and three strategy meetings each year.',
    priceLabel: `${PRICES.ongoingMonthly} / month`,
  },
];

export const COMPARISON_ROWS = [
  {
    label: 'Best for',
    comprehensive: 'A coordinated roadmap across major planning areas',
    ongoing: 'Coordinated planning plus continued support',
  },
  {
    label: 'Price',
    comprehensive: PRICES.comprehensive,
    ongoing: `${PRICES.ongoingMonthly} per month`,
  },
  {
    label: 'Typical engagement',
    comprehensive: 'A complete planning engagement',
    ongoing: 'Year-round planning rhythm',
  },
  {
    label: 'Deliverables',
    comprehensive: 'Cash, risk, and wealth-management roadmap',
    ongoing: 'Reports, budgeting support, three meetings/year',
  },
];

export const OFFERS = [
  {
    id: OFFER_IDS.COMPREHENSIVE,
    anchor: 'comprehensive-plan',
    pricingModel: PRICING_MODELS.FIXED,
    title: 'Comprehensive financial plan',
    priceLabel: PRICES.comprehensive,
    priceNote: null,
    bestFor: 'Households that want a coordinated plan across major planning areas.',
    includes: [
      'Cash management',
      'Risk management',
      'Wealth management',
    ],
    ctaLabel: 'Explore a full financial plan',
    exampleOnly: false,
  },
  {
    id: OFFER_IDS.ONGOING,
    anchor: 'ongoing-planning',
    pricingModel: PRICING_MODELS.MONTHLY,
    title: 'Ongoing financial planning',
    priceLabel: `${PRICES.ongoingMonthly} per month`,
    priceNote: null,
    bestFor: 'Households that want implementation support and continued planning attention.',
    includes: [
      'Investment reports',
      'Done-for-you budgeting',
      'Net-worth tracking',
      'Three strategy meetings per year',
    ],
    ctaLabel: 'Explore ongoing planning',
    exampleOnly: false,
  },
];

export const ONGOING_MEETINGS = [
  {
    id: 'winter',
    season: 'January / February',
    title: 'Estate planning and risk management',
    description:
      'Review risk coverage, beneficiary designations, and estate-planning coordination needs for the year ahead.',
  },
  {
    id: 'summer',
    season: 'May / June',
    title: 'Tax planning and investments',
    description:
      'Review tax-planning opportunities and investment positioning before mid-year decisions stack up.',
  },
  {
    id: 'fall',
    season: 'September / October',
    title: 'Progress review, goal setting, and employee benefits',
    description:
      'Check progress, reset goals, and review employee-benefit elections before year-end.',
  },
];

export const SERVICE_GUIDE_OPTIONS = [
  {
    id: 'coordinated_plan',
    label: 'I want a coordinated financial plan',
    mapsToOfferId: OFFER_IDS.COMPREHENSIVE,
    guidance:
      'The comprehensive plan covers cash management, risk management, and wealth management in one engagement.',
  },
  {
    id: 'continued_support',
    label: 'I want implementation and continued support',
    mapsToOfferId: OFFER_IDS.ONGOING,
    guidance:
      'Ongoing planning adds reporting, budgeting support, net-worth tracking, and three strategy meetings each year.',
  },
];

/**
 * Remaining decisions before promotion. Draft answers from Form ADV / Schedule A
 * are in docs/services-pricing-launch-checklist.md — still require compliance sign-off.
 */
export const OPEN_COMPLIANCE_QUESTIONS = [
  'Define the exact contents of the monthly “investment report” / progress report deliverable.',
  `Confirm website packaging: ${PRICES.comprehensive} comprehensive plan + ${PRICES.ongoingMonthly}/mo ongoing as separate offers vs ADV “upfront + monthly” as one comprehensive engagement.`,
  'Compliance review of final public language before promoting campaign links.',
];

export const PRICING_FAQS = [
  {
    id: 'planning_start_refund',
    question: `What if I do not feel the ${PRICES.planningMeeting} ${GOAL_SETTING_MEETING.name} was valuable?`,
    answer: `${GOAL_SETTING_MEETING.refundWording} ${GOAL_SETTING_MEETING.creditWording}`,
  },
  {
    id: 'planning_start_reschedule',
    question: `What if I need to move or cancel my ${GOAL_SETTING_MEETING.name}?`,
    answer: GOAL_SETTING_MEETING.cancellationWording,
  },
  {
    id: 'choose_engagement',
    question: 'How do I choose between a comprehensive plan and ongoing planning?',
    answer:
      'Choose the comprehensive plan when you need a coordinated roadmap. Add ongoing planning when you want continued reporting, implementation support, and three strategy meetings each year.',
  },
  {
    id: 'comprehensive_includes',
    question: `What does the ${PRICES.comprehensive} comprehensive plan include?`,
    answer:
      'The comprehensive financial plan covers cash management, risk management, and wealth management. It is designed for households that want a coordinated plan across those areas.',
  },
  {
    id: 'monthly_includes',
    question: `What is included in the ${PRICES.ongoingMonthly} monthly service?`,
    answer:
      'Ongoing planning includes investment updates as part of regular progress reporting, done-for-you budgeting, net-worth tracking, and three strategy meetings per year. The service may be ended with 30 days’ notice; fees are prorated and any unearned fee is refunded, consistent with the firm’s financial-planning fee schedule.',
  },
  {
    id: 'strategy_meetings',
    question: 'What happens during the three annual strategy meetings?',
    answer:
      'January/February focuses on estate planning and risk management. May/June focuses on tax planning and investments. September/October covers progress review, goal setting, and employee benefits. Tax and estate topics are planning coordination—not tax preparation or legal services; those professionals are engaged separately when needed.',
  },
  {
    id: 'investment_third_party_fees',
    question: 'Are investment management fees or third-party costs included?',
    answer:
      'No. The prices on this page are financial planning fees. Investment management, when engaged, is a separate advisory service under the firm’s Form ADV. Clients may also incur third-party costs such as fund expenses, custody, brokerage, or outside-manager fees; those costs are in addition to planning fees. Confirm the full fee picture for your engagement before you sign your agreement.',
  },
];

/**
 * Build intake URL with offer + optional campaign/content context for attribution.
 */
export function buildIntakePath({
  offerId,
  pricingModel,
  contentCategory,
  promotionalAngle,
  cta,
  calculatorId,
  calculatorName,
} = {}) {
  const params = new URLSearchParams();
  if (cta) params.set('cta', cta);
  if (offerId) params.set('offer', offerId);
  if (pricingModel) params.set('pricing_model', pricingModel);
  if (contentCategory) params.set('content_category', contentCategory);
  if (promotionalAngle) params.set('promotional_angle', promotionalAngle);
  if (calculatorId) params.set('calculator_id', calculatorId);
  if (calculatorName) params.set('calculator_name', calculatorName);
  const query = params.toString();
  return query ? `${INTAKE_PATH}?${query}` : INTAKE_PATH;
}

function pathWithContext(path, { contentCategory, promotionalAngle, cta, calculatorId, calculatorName } = {}) {
  const params = new URLSearchParams();
  if (cta) params.set('cta', cta);
  if (contentCategory) params.set('content_category', contentCategory);
  if (promotionalAngle) params.set('promotional_angle', promotionalAngle);
  if (calculatorId) params.set('calculator_id', calculatorId);
  if (calculatorName) params.set('calculator_name', calculatorName);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

/** Goal-Setting Meeting details page, with optional content context for attribution. */
export function buildMeetingPath(context) {
  return pathWithContext(MEETING_PATH, context);
}

/** Booking page (pick a time, pay, sign). */
export function buildBookingPath(context) {
  return pathWithContext(BOOKING_PATH, context);
}

export function buildServicesPath(hash) {
  if (!hash) return SERVICES_PATH;
  const clean = String(hash).replace(/^#/, '');
  return `${SERVICES_PATH}#${clean}`;
}

export function getOfferById(offerId) {
  return OFFERS.find((offer) => offer.id === offerId) || null;
}

/**
 * Contextual next-step links for calculators and educational pages.
 * Guidance only — not an automated recommendation.
 */
export const CONTEXTUAL_SERVICE_LINKS = {
  [CONTENT_CATEGORIES.BUDGET]: {
    intro: 'Budget and cash-flow decisions often benefit from a fuller plan or ongoing support.',
    links: [
      { offerId: OFFER_IDS.ONGOING, label: 'Explore ongoing planning', hash: 'ongoing-planning' },
      { offerId: OFFER_IDS.COMPREHENSIVE, label: 'Explore a full financial plan', hash: 'comprehensive-plan' },
    ],
  },
  [CONTENT_CATEGORIES.TAX]: {
    intro: 'Tax decisions are considered as part of a coordinated financial plan.',
    links: [
      { offerId: OFFER_IDS.COMPREHENSIVE, label: 'Get a financial plan', hash: 'comprehensive-plan' },
    ],
  },
  [CONTENT_CATEGORIES.INVESTMENT]: {
    intro: 'Investment decisions usually connect to wealth management and ongoing reporting.',
    links: [
      { offerId: OFFER_IDS.ONGOING, label: 'Explore ongoing planning', hash: 'ongoing-planning' },
      { offerId: OFFER_IDS.COMPREHENSIVE, label: 'Explore wealth management in a full plan', hash: 'comprehensive-plan' },
    ],
  },
  [CONTENT_CATEGORIES.RISK]: {
    intro: 'Insurance, estate, and risk topics are covered in a comprehensive plan and the winter strategy meeting.',
    links: [
      { offerId: OFFER_IDS.COMPREHENSIVE, label: 'Explore a full financial plan', hash: 'comprehensive-plan' },
      { offerId: OFFER_IDS.ONGOING, label: 'See the January/February risk meeting', hash: 'ongoing-timeline' },
    ],
  },
  [CONTENT_CATEGORIES.RETIREMENT]: {
    intro: 'Retirement tradeoffs often need a coordinated plan or ongoing implementation support.',
    links: [
      { offerId: OFFER_IDS.COMPREHENSIVE, label: 'Explore a full financial plan', hash: 'comprehensive-plan' },
      { offerId: OFFER_IDS.ONGOING, label: 'Explore ongoing planning', hash: 'ongoing-planning' },
    ],
  },
  [CONTENT_CATEGORIES.HOUSING]: {
    intro: 'Housing decisions affect cash flow, risk, and long-term wealth and belong in a coordinated plan.',
    links: [
      { offerId: OFFER_IDS.COMPREHENSIVE, label: 'Explore a full financial plan', hash: 'comprehensive-plan' },
    ],
  },
  [CONTENT_CATEGORIES.GENERAL]: {
    intro: 'Not sure which engagement fits? Start with the service-selection guide.',
    links: [
      { offerId: null, label: 'Open the service-selection guide', hash: 'service-guide' },
    ],
  },
  [CONTENT_CATEGORIES.EDUCATIONAL]: {
    intro: 'When you are ready for a next step, choose the engagement that matches your need.',
    links: [
      { offerId: null, label: 'Compare services and pricing', hash: 'pricing-comparison' },
    ],
  },
};

const planningOffers = {
  PRICES,
  GOAL_SETTING_MEETING,
  PLANNING_MEETING_REFUND_WORDING,
  SERVICES_PATH,
  SERVICES_ALIAS_PATH,
  INTAKE_PATH,
  MEETING_PATH,
  BOOKING_PATH,
  AUDIENCE,
  NEW_PLAN_CLIENTS_PER_MONTH,
  OFFER_IDS,
  PRICING_MODELS,
  OFFERS,
  PRIMARY_WAYS,
  COMPARISON_ROWS,
  ONGOING_MEETINGS,
  SERVICE_GUIDE_OPTIONS,
  PRICING_FAQS,
  CONTEXTUAL_SERVICE_LINKS,
  buildIntakePath,
  buildMeetingPath,
  buildBookingPath,
  buildServicesPath,
  getOfferById,
};

export default planningOffers;
